import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useWeb3React } from "@web3-react/core";
import { TabView, TabPanel } from 'primereact/tabview';
import { Popover, Table, Tooltip as TooltipNext, Button as RevealButton, Progress, Collapse, Text, Grid, Loading } from '@nextui-org/react';
import Web3 from "web3";

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import ContractContext from '../../ContractContext'; //Required for all dApp components using contracts

import {
  infoOutlineStyle, cardOutlineHeaderStyle, cardSmartRewardOutlineStyle, miniCardHeaderOutlineStyle, miniInfoOutlineStyle,
  infoOutlineInvertStyle, cardOutlineInvertHeaderStyle, cardOutlineInvertStyle, miniCardHeaderStyle, miniInfoStyle,
  infoStyle, cardStyle, cardHeaderInvertStyle, miniCardHeaderInvertStyle, miniInfoInvertStyle, cardHeaderStyle
} from "./TileStyles";

import { formatTokenLabel, formatBalanceWithIcon, getTokenPrice, formatDecimals, formatCurrencyExtended, formatPercentage, formatWalletAddress } from '../../utils';

import BigNumber from 'bignumber.js';

import { useInView } from 'react-intersection-observer';

import { DefiAppService } from "../../DefiAppService";

import ierc20Abi from '../../contracts/ierc20Abi';
import SmartRewardsSelector from "./SmartRewardsSelector";
import RewardPacksSelector from "./RewardPacksSelector";
import AddReward from "./AddReward";

const RewardsModuleRewards = ({ data, getData }) => {

  const { library, account } = useWeb3React();
  const contractContext = useContext(ContractContext);
  const contracts = contractContext.contracts;
  const isWalletConnected = () => account !== null;

  const toast = useRef(null);

  const LoadingContent = () => <Skeleton width="50%" height="35px" className="mb-2" />;
  const LoadingTitle = () => <Skeleton width="25%" height="25px" className="mb-2" />;

  const [refTotalEarnedV3, inViewTotalEarnedV3] = useInView({
    triggerOnce: true, // Change it to false if you want to recalculate every time it enters the view
  });

  const [refTotalEarnedV2, inViewTotalEarnedV2] = useInView({
    triggerOnce: true, // Change it to false if you want to recalculate every time it enters the view
  });

  const [refTotalEarnedV1, inViewTotalEarnedV1] = useInView({
    triggerOnce: true, // Change it to false if you want to recalculate every time it enters the view
  });


  const [totalEarned, setTotalEarned] = useState(0);

  const [claimingStates, setClaimingStates] = useState({});


  const [plusBoost, setPlusBoost] = useState(0);


  useEffect(() => {
    getPlusBoost().then(setPlusBoost);
  }, [contracts.rp_rocketFiFeeRouter]);

  const getPlusBoost = useCallback(async () => {
    const result = await contracts.rp_rocketFiFeeRouter.methods.plusBooster().call();
    //console.log("getSmartCodeData:", result);
    return result;
  }, [contracts.rp_rocketFiFeeRouter]);


  useEffect(() => {
    //console.log("RocketFi-Dashboard data", data);
    if (data?.contractData !== undefined) {
      setTotalEarned(calculateTotalEarnedByVersion(data?.tokensList) +
        (isV2Relevant() ? calculateTotalEarnedByVersion(data?.tokensListV2) : 0) +
        (isV1Relevant() ? calculateTotalEarnedByVersion(data?.tokensListV1) : 0));
    }
  }, [data]);


  const calculateTotalDistributedByVersion = (tokensList) => {
    let totalDistributed = new BigNumber(0);

    if (tokensList) {
      tokensList.forEach(token => {
        if (token.price && (token.totalDistributed > 0)) {
          const value = new BigNumber(token.totalDistributed)
            .times(token.price)
            .div(new BigNumber(10).pow(token.decimals));

          totalDistributed = totalDistributed.plus(value);
        }
      });
    }

    // Convert back to a number, for display
    return totalDistributed.isNaN() ? 0 : totalDistributed.toNumber();
  };

  const calculateTotalEarnedClaimedByVersion = (tokensList) => {
    let totalEarned = new BigNumber(0);

    if (tokensList) {
      tokensList.forEach(token => {
        if (token.price && (token.claimedAmount > 0)) {
          const value = new BigNumber(token.claimedAmount)
            .times(token.price)
            .div(new BigNumber(10).pow(token.decimals));

          totalEarned = totalEarned.plus(value);
        }
      });
    }

    // Convert back to a number, for display
    return totalEarned.isNaN() ? 0 : totalEarned.toNumber();
  };

  const calculateTotalEarnedUnclaimedByVersion = (tokensList) => {
    let totalEarned = new BigNumber(0);

    if (tokensList) {
      tokensList.forEach(token => {
        if (token.price && (token.unclaimedAmount > 0)) {
          const value = new BigNumber(token.unclaimedAmount)
            .times(token.price)
            .div(new BigNumber(10).pow(token.decimals));

          totalEarned = totalEarned.plus(value);
        }
      });
    }

    // Convert back to a number, for display
    return totalEarned.isNaN() ? 0 : totalEarned.toNumber();
  };

  const calculateClaimedValueUSD = (token) => {
    if (!token.price || token.claimedAmount <= 0) return 0;
    return new BigNumber(token.claimedAmount)
      .times(token.price)
      .div(new BigNumber(10).pow(token.decimals))
      .toNumber();
  };

  const calculateUnclaimedValueUSD = (token) => {
    if (!token.price || token.unclaimedAmount <= 0) return 0;
    return new BigNumber(token.unclaimedAmount)
      .times(token.price)
      .div(new BigNumber(10).pow(token.decimals))
      .toNumber();
  };

  const calculateTotalEarnedByVersion = (tokensList) => {
    let totalEarned = new BigNumber(0);

    if (data && tokensList) {
      tokensList.forEach(token => {
        if (token.price && (token.unclaimedAmount > 0 || token.claimedAmount > 0)) {
          const value = new BigNumber(token.unclaimedAmount)
            .plus(token.claimedAmount)
            .times(token.price)
            .div(new BigNumber(10).pow(token.decimals));

          totalEarned = totalEarned.plus(value);
        }
      });
    }

    return totalEarned.isNaN() ? 0 : totalEarned.toNumber();
  };

  const unclaimedRewardsCount = () => {
    if (!data?.contractData?.smartData?.subscriptionPools) return 0;

    let total = 0;
    for (const poolID of data?.contractData?.smartData.subscriptionPools) {
      const currentTokenData = data?.tokenPoolData?.find((token) => token._poolID === poolID, 10);
      if (currentTokenData && !isNaN(currentTokenData._unclaimedAmount) && currentTokenData._unclaimedAmount > 0) {
        total += 1;
      }
    }

    return total;
  };

  const getUnclaimedValue = (token) => {
    return formatCurrencyExtended(new BigNumber(token.unclaimedAmount)
      .dividedBy(new BigNumber(10).pow(token.decimals))
      .multipliedBy(getTokenPrice(data.tokensList, token.symbol))
      .toString())
  }

  const isV2Relevant = () => {
    const totalV2 = calculateTotalEarnedByVersion(data?.tokensListV2);
    return totalV2 && totalV2 > 0 ? true : false;
  };

  const isV1Relevant = () => {
    const totalV1 = calculateTotalEarnedByVersion(data?.tokensListV1);
    return totalV1 && totalV1 > 0 ? true : false;
  };


  const handleBuyReflectionsWithToken = async (token) => {
    if (!isWalletConnected()) {
      return;
    }
    if (!contracts.rp_rewardPacks) {
      return;
    }

    try {
      const transaction = contracts.rp_rewardPacks.methods.buyReflectionsWithToken(token.address).send({ from: account });
      transaction.on("transactionHash", (hash) => {
        toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 3000 });
      });

      const tx = await transaction;

      toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully. Thanks for processing the Reward!', life: 3000 });

      getData();
    } catch (error) {
      console.error("Error interacting with the Reward Pack contract:", error);
      toast.current.show({ severity: 'error', summary: 'Transaction Error', detail: `Error interacting with the Reward Pack contract: ${error.message}`, life: 3000 });
    }
  };


  const handleClaim = async (smartRewards, token) => {
    //console.log("Smart Rewards: Claim Rewards button clicked - smartRewards:", smartRewards);
    //console.log("Smart Rewards: Claim Rewards button clicked - token:", token);
    if (!isWalletConnected()) {
      console.error("Please connect to a wallet before interacting with the contract.");
      return;
    }
    if (!smartRewards) {
      console.error("Smart Rewards contract not available.");
      return;
    }

    setClaimingStates(prev => ({ ...prev, [token.name]: true }));
    console.log("Smart Rewards: Claim Rewards button clicked");

    try {
      const transaction = smartRewards.methods.claimDividends(token.name).send({ from: account });

      transaction.on("transactionHash", (hash) => {
        toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 3000 });
      });

      const tx = await transaction;

      toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully.', life: 3000 });


    } catch (error) {
      console.error("Error interacting with the Smart Rewards contract:", error);
      toast.current.show({ severity: 'error', summary: 'Transaction Error', detail: `Error interacting with the Smart Rewards contract: ${error.message}`, life: 3000 });
    }
    setClaimingStates(prev => ({ ...prev, [token.name]: false }));
    getData();
  };

  const handleClaimLegacy = async (smartRewards, reward) => {
    if (!isWalletConnected()) {
      console.error("Please connect to a wallet before interacting with the contract.");
      return;
    }
    if (!smartRewards) {
      console.error("Smart Rewards contract not available.");
      return;
    }

    setClaimingStates(prev => ({ ...prev, [reward.name]: true }));
    console.log("Smart Rewards: Claim Rewards button clicked");

    try {

      const reflectionPoolInstance = await buildContract(data?.tokensListV2?.find((token) => token.address.toLowerCase() === reward.name.toLowerCase()).reflectionPoolAddress, smartRewards);
      const transaction = reflectionPoolInstance.methods.claimDividends().send({ from: account });

      transaction.on("transactionHash", (hash) => {
        toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 3000 });
      });

      const tx = await transaction;

      toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully.', life: 3000 });


    } catch (error) {
      console.error("Error interacting with the Smart Rewards contract:", error);
      toast.current.show({ severity: 'error', summary: 'Transaction Error', detail: `Error interacting with the Smart Rewards contract: ${error.message}`, life: 3000 });
    }
    setClaimingStates(prev => ({ ...prev, [reward.name]: false }));
    getData();
  };

  const buildContract = async (contractAddress, smartRewards) => {
    console.log("buildContract: ", smartRewards._address); // Check contractAddress")
    const reflectionPoolContractInfo = DefiAppService.getSupplementalContracts("RocketFi", smartRewards._address); //get distributor contract info
    console.log("reflectionPoolContractInfo: ", reflectionPoolContractInfo); // Check reflectionPoolContractInfo
    //console.log("distributionAddress: ", reflectionPoolContractInfo.distributorAddress); // Check reflectionPoolContractInfo.distributorAddress
    const web3 = new Web3(window.ethereum);

    const contractInstance = new web3.eth.Contract(reflectionPoolContractInfo.abi, contractAddress)

    await web3.eth.getCode(contractAddress).then((code) => {
      if (code === "0x") {
        console.error(`No found at the specified address:`, contractAddress);
      } else {
        console.log(`Distributor exists at the specified address`);
      }
    });

    console.log(`${contractInstance} contract initialized:`, contractInstance);
    return contractInstance;
  }

  const consolidateTokens = () => {
    if (!data?.tokensList) return [];
    // Assuming all tokens have a unique name across all versions
    const allTokens = [...data?.tokensList, ...data?.tokensListV2, ...data?.tokensListV1];
    const consolidated = {};

    allTokens.forEach(token => {
      const name = token.value;
      const usdValue = calculateClaimedValueUSD(token);  // You can modify this if you've other logic
      if (consolidated[name]) {
        consolidated[name].totalUSD += usdValue;
      } else {
        consolidated[name] = {
          name,
          totalUSD: usdValue,
          symbol: token.label,
          unclaimedAmount: token.unclaimedAmount,
          decimals: token.decimals,
        };
      }
    });

    return Object.values(consolidated);
  };

  const consolidateClaimableTokens = () => {
    if (!data?.tokensList) return [];
    // This function's logic can be similar to consolidateTokens() but will focus on unclaimed tokens
    const allTokens = [...data?.tokensList, ...data?.tokensListV2, ...data?.tokensListV1];
    const consolidated = {};

    allTokens.forEach(token => {
      const name = token.value;
      const usdValue = calculateUnclaimedValueUSD(token);  // You can modify this if you've other logic
      if (consolidated[name]) {
        consolidated[name].totalUSD += usdValue;
      } else {
        consolidated[name] = {
          name,
          totalUSD: usdValue,
          symbol: token.label,
          unclaimedAmount: token.unclaimedAmount,
          decimals: token.decimals,
        };
      }
    });

    return Object.values(consolidated);
  };

  const handleConsolidatedClaim = async (token) => {
    //setClaimingStates(prev => ({ ...prev, [token.name]: true }));
    const tokenVersion = await getTokenVersionWithUnclaimedAmount(token.name);

    switch (tokenVersion) {
      case 'V3':
        handleClaim(contracts.smartRewards, token);
        break;
      case 'V2':
      case 'V1':
        handleClaimLegacy(contracts[`smartRewards${tokenVersion}`], token);
        break;
      default:
        console.error("Token version with unclaimed amount not recognized");
        break;
    }

    //setClaimingStates(prev => ({ ...prev, [token.name]: false }));
  };
  const hasUnclaimedAmount = async (tokensList, tokenName) => {
    const token = tokensList.find(t => t.value === tokenName);
    if (!token) return false;

    // Depending on how you're storing unclaimed amounts, 
    // this is a dummy check. Replace with your actual check.
    return token.unclaimedAmount > 0;
  };

  const getTokenVersionWithUnclaimedAmount = async (tokenName) => {
    if (await hasUnclaimedAmount(data?.tokensList, tokenName)) {
      return 'V3';
    } else if (await hasUnclaimedAmount(data?.tokensListV2, tokenName)) {
      return 'V2';
    } else if (await hasUnclaimedAmount(data?.tokensListV1, tokenName)) {
      return 'V1';
    } else {
      return null;
    }
  };






  const [transferEvents, setTransferEvents] = useState([]);
  const [transferEventsWithTimestamps, setTransferEventsWithTimestamps] = useState([]);
  const [transferEventsWithDetails, setTransferEventsWithDetails] = useState([]);

  async function GetLatestBlockNUmber() {
    if (!library) return;
    const web3 = new Web3(library.provider);
    const currentBlock = await web3.eth.getBlock("latest");
    //console.log("currentBlock:", currentBlock.number);
    return currentBlock.number
  }

  const GetPastTransferEvents = async (reward) => {
    if (!reward) return [];
    const web3 = new Web3("https://binance.llamarpc.com/sk_llama_e2e9915b5438c5313ae3d634858d85cb");
    const contract = new web3.eth.Contract(ierc20Abi, reward);
    const events = await contract.getPastEvents('Transfer', {
      fromBlock: await GetLatestBlockNUmber() - 200000,
      toBlock: await GetLatestBlockNUmber(),
      filter: { to: account, from: contracts.rp_rewardPacks._address },
    });
    return events;
  };

  useEffect(() => {
    if (!transferEvents) return;
    //console.log("transferEvents:", transferEvents);
    fetchTimestampsForEvents(transferEvents).then(setTransferEventsWithTimestamps);
  }, [transferEvents]);

  useEffect(() => {
    if (!transferEventsWithTimestamps) return;
    //console.log("transferEventsWithTimestamps:", transferEventsWithTimestamps);
  }, [transferEventsWithTimestamps]);

  async function getBlockTimestamp(blockNumber) {
    if (!library) return;
    const web3 = new Web3(library.provider);
    const block = await web3.eth.getBlock(blockNumber);
    return block.timestamp;
  }

  async function fetchTimestampsForEvents(events) {
    if (!library) return;
    const promises = events.map(event => getBlockTimestamp(event.blockNumber));
    const timestamps = await Promise.all(promises);
    return events.map((event, index) => ({
      ...event,
      timestamp: timestamps[index]
    }));
  }



  useEffect(() => {
    if (!data.tokensList) return;
    const fetchAndConsolidateEvents = async () => {
      let allEventsWithDetails = [];
      const tokensWithEarnings = data.tokensList.filter(token => calculateTotalEarnedByVersion([token]) > 0);
      for (const token of tokensWithEarnings) {
        const events = await GetPastTransferEvents(token.address);
        //console.log("token:", token);
        const eventsWithDetails = events.map(event => ({
          ...event,
          token: {
            symbol: token.label, // Assuming token.symbol is the correct path to the token's symbol
            decimals: token.decimals, // Assuming token.decimals is the correct path to the token's decimals
            price: token.price, // Assuming token.price is the correct path to the token's price
          },
        }));
        allEventsWithDetails = [...allEventsWithDetails, ...eventsWithDetails];
      }
      setTransferEvents(allEventsWithDetails);
    };

    fetchAndConsolidateEvents();
  }, [data?.tokensList]);


  useEffect(() => {
    if (!transferEventsWithTimestamps) return;

    const consolidateEvents = transferEventsWithTimestamps.map(event => {
      const tokenAmount = new BigNumber(event.returnValues.value).dividedBy(new BigNumber(10).pow(event.token.decimals));
      const tokenSymbol = event.token.symbol;
      const usdValue = tokenAmount.multipliedBy(event.token.price);
      const date = new Date(event?.timestamp * 1000);

      return {
        reward: tokenSymbol,
        amount: tokenAmount.toFixed(), // May want to limit the number of decimal places
        usdValue: usdValue.toFixed(2), // For example, limiting to 2 decimal places
        date: date.toLocaleDateString(), // Adjust format as necessary
        decimals: event.token.decimals,
      };
    });

    setTransferEventsWithDetails(consolidateEvents);
  }, [transferEventsWithTimestamps]);

  useEffect(() => {
    if (!transferEventsWithDetails) return;
    //console.log("transferEventsWithDetails:", transferEventsWithDetails);
  }, [transferEventsWithDetails]);


  const DisplayValue = ({ isLoading, value }) => (
    isLoading ? <LoadingContent /> : formatCurrencyExtended(value)
  );

  // Custom component for total earned and distributed summary
  const SummaryInfo = ({ title, value }) => (
    <div>
      <div style={{ marginTop: "15px" }}>{title}</div>
      <div style={infoStyle}>
        <DisplayValue isLoading={data?.contractData === undefined} value={value} />
      </div>
    </div>
  );

  const collapseStyle = {
    background: 'transparent',
    boxShadow: 'none',
    //padding: '20px',
    borderRadius: '20px',
  };

  const getTopSubscribedTokens = () => {
    if (!data?.tokensListRp) return [];

    return data.tokensListRp
      .slice()
      .sort((a, b) => b.rocketFiSubscribed - a.rocketFiSubscribed)
    //.slice(0, 50);
  };

  const getRecentlyAddedTokens = () => {
    if (!data?.tokensListRp) return [];

    return data.tokensListRp
      .slice()
      .sort((a, b) => b.poolID - a.poolID)
      .slice(0, 25);
  };

  const [activeIndex, setActiveIndex] = useState(0);
  const [activeIndexRP, setActiveIndexRP] = useState(0);
  return (
    <>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px',
        color: 'white',
        background: 'linear-gradient(263deg, rgb(150 50 75 / 85%), rgb(180 75 75 / 85%)), linear-gradient(153deg,rgb(0, 0, 0), rgb(0, 0, 0) 19%, rgb(0, 0, 0))',
        padding: '10px',
        borderRadius: '20px',
        boxShadow: 'none',
      }}>

        <div>
          <div style={{ marginTop: "15px" }}>
            Your Total Earned
          </div>
          <div style={infoStyle}>
            {data?.contractDataRp !== undefined
              ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensListRp)
                + calculateTotalEarnedByVersion(data?.tokensListV3)
                + calculateTotalEarnedByVersion(data?.tokensListV2)
                + calculateTotalEarnedByVersion(data?.tokensListV1))
              : LoadingContent()
            }
          </div>
        </div>



        <div>
          <div style={{ marginTop: "15px" }}>
            Total Distributed <br /> (Reward Packs + Smart Rewards)
          </div>
          <div style={infoStyle}>
            {data?.contractDataRp !== undefined
              ? formatCurrencyExtended(
                calculateTotalDistributedByVersion(data?.tokensListRp) +
                calculateTotalDistributedByVersion(data?.tokensListV3) +
                calculateTotalDistributedByVersion(data?.tokensListV2) +
                calculateTotalDistributedByVersion(data?.tokensListV1))
              : LoadingContent()
            }
          </div>
        </div>
      </div>
      <div style={{ marginLeft: '-10px', marginRight: '-10px' }}>
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} >
          <TabPanel header="Reward Packs"  >
            <div style={{ marginLeft: '-10px', marginRight: '-10px' }}>
              <TabView activeIndex={activeIndexRP} onTabChange={(e) => setActiveIndexRP(e.index)}>

                <TabPanel header="Manage" >
                  <div style={{
                    background: "linear-gradient(263deg, rgb(25 25 75 / 85%), rgb(25 0 50 / 85%)), linear-gradient(153deg,rgb(0, 0, 0), rgb(0, 0, 0) 19%, rgb(0, 0, 0))",
                    boxShadow: 'none',
                    padding: '10px',
                    borderRadius: '20px',
                    marginTop: '20px',
                  }}>


                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>



                      <div>
                        <div style={{ marginTop: "15px" }}>
                          Your Total Earned
                        </div>
                        <div style={infoStyle}>
                          {data?.contractDataRp !== undefined ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensListRp)) : LoadingContent()}
                        </div>
                      </div>

                      <div>
                        <div style={{ marginTop: "15px" }}>
                          Rewards Distributed
                        </div>
                        <div style={infoStyle}>
                          {data?.contractDataRp !== undefined
                            ? formatCurrencyExtended(calculateTotalDistributedByVersion(data?.tokensListRp))
                            : LoadingContent()
                          }
                        </div>
                      </div>

                      <div>
                        <div style={{ marginTop: "15px" }}>
                          Reward Position
                        </div>
                        <div style={infoStyle}>
                          {data?.contractDataRp !== undefined
                            ? formatDecimals(data?.contractDataRp?.smartData?.userShares, 9)
                            : LoadingContent()
                          }
                        </div>
                        <div>
                          {data?.contractDataRp !== undefined
                            ?
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                Mirror Boost: {formatDecimals(data?.contractDataRp?.burnData?.rocketFuelAmount, 9)}
                              </div>
                              <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                RocketFi+ Boost: {formatPercentage(plusBoost / 10000)}
                              </div>
                            </div>
                            : LoadingContent()
                          }
                        </div>
                      </div>

                      <div>
                        <div style={{ marginTop: "15px" }}>
                          Position % (of total)
                        </div>
                        <div style={infoStyle}>
                          {data?.contractDataRp !== undefined
                            ? formatPercentage(data?.contractDataRp?.smartData?.userShares / data?.contractDataRp?.smartData?.smartRewardsTotalShares, 9)
                            : LoadingContent()
                          }
                        </div>
                      </div>
                    </div>


                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginTop: '15px' }}>
                    <Card style={cardSmartRewardOutlineStyle}
                      title={"Summary"}
                    >
                      <div style={{ marginTop: "10px" }}>

                        <div>





                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                            <div>
                              <div style={miniCardHeaderStyle}>
                                {data?.contractDataRp !== undefined
                                  ? 'Claimed'
                                  : <LoadingTitle />
                                }
                              </div>
                              <div style={miniInfoStyle}>
                                {data?.contractDataRp !== undefined ? formatCurrencyExtended(calculateTotalEarnedClaimedByVersion(data?.tokensListRp)) : LoadingContent()}
                              </div>
                            </div>
                            <div>
                              <div style={miniCardHeaderStyle}>
                                {data?.contractDataRp !== undefined
                                  ? 'Unclaimed'
                                  : <LoadingTitle />
                                }
                              </div>
                              <div style={miniInfoStyle}>
                                {data?.contractDataRp !== undefined ? formatCurrencyExtended(calculateTotalEarnedUnclaimedByVersion(data?.tokensListRp)) : LoadingContent()}
                              </div>
                            </div>
                          </div>
                        </div>
                        {calculateTotalEarnedClaimedByVersion(data?.tokensListRp) > 0 &&
                          (
                            <>
                              <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                                Earnings Summary
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                                {data.tokensListRp &&
                                  data.tokensListRp
                                    .map(token => ({
                                      name: token.value,
                                      claimedUSD: calculateClaimedValueUSD(token),
                                      symbol: token.label,
                                    }))
                                    .sort((a, b) => b.claimedUSD - a.claimedUSD)
                                    .filter((token) => token.claimedUSD > 0)
                                    .map((token, index) => (
                                      <div key={index} style={{
                                        ...miniInfoStyle, display: 'flex',
                                        flexDirection: 'column',
                                      }}>
                                        <div style={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                        }}>
                                          {formatTokenLabel(data.dbData, { value: token.name, label: token.label })}
                                          {formatCurrencyExtended(token.claimedUSD)}
                                        </div>

                                        <div style={{
                                          fontSize: '12px',
                                          marginTop: '2px',
                                          color: 'grey',
                                          fontWeight: '200',
                                          //marginLeft: '5px',
                                          //increase letter spacing
                                          letterSpacing: '2px',
                                        }}>
                                          <strong>{token.symbol === "WBNB" ? "BNB" : token.symbol}</strong>
                                        </div>


                                      </div>
                                    ))
                                }

                              </div>
                            </>
                          )}
                        {calculateTotalEarnedUnclaimedByVersion(data?.tokensListRp) > 0 &&
                          (
                            <>
                              <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                                Claimable Rewards
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '5px', marginTop: '10px' }}>
                                {data.tokensListRp &&
                                  data.tokensListRp
                                    .map(token => ({
                                      name: token.value,
                                      unclaimedUSD: calculateUnclaimedValueUSD(token),
                                      unclaimedAmount: token.unclaimedAmount,
                                      symbol: token.label,
                                      decimals: token.decimals,
                                    }))
                                    .sort((a, b) => b.unclaimedUSD - a.unclaimedUSD)
                                    .filter((token) => token.unclaimedUSD > 0)
                                    .map((token, index) => (
                                      <div key={index} style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>

                                        <Button
                                          onClick={() => handleClaim(contracts.rp_rewardPacks, token)}
                                          rounded
                                          severity="warning"
                                          size="small"
                                          style={{ width: '125px', justifyContent: 'center', marginTop: '5px' }}
                                        >
                                          <span>{formatTokenLabel(data.dbData, { value: token.name, label: token.label })}</span>
                                          <span>{`${formatDecimals(parseFloat(token.unclaimedAmount), token.decimals)} (${formatCurrencyExtended(token.unclaimedUSD)})`}</span>
                                        </Button>
                                      </div>
                                    ))
                                }
                              </div>
                            </>
                          )}

                        {
                          transferEventsWithDetails.length > 0 &&
                          <div style={{
                            gridColumn: 'span 2',
                            gridRow: 'span 1',
                            marginTop: '20px',
                          }}>
                            <div style={cardHeaderStyle}>
                              Recent Deposits/Claims</div>
                            <Table
                              aria-label="Reward Transfer History"
                              css={{
                                height: "auto",
                                minWidth: "100%",
                                backgroundColor: "white",
                              }}
                            >
                              <Table.Header>
                                <Table.Column>Reward</Table.Column>
                                <Table.Column>Amount</Table.Column>
                                {/* <Table.Column>USD Value</Table.Column> */}
                                <Table.Column>Date</Table.Column>
                              </Table.Header>
                              <Table.Body>
                                {transferEventsWithDetails
                                  .sort((a, b) => b.blockNumber - a.blockNumber)
                                  .map((event, index) => (
                                    <Table.Row key={index}>
                                      <Table.Cell><div style={{
                                        fontSize: "12px",
                                        fontWeight: "normal",
                                      }}>
                                        {event.reward}
                                      </div>
                                      </Table.Cell>
                                      <Table.Cell>
                                        <div style={{
                                          fontSize: "12px",
                                          fontWeight: "normal",
                                        }}>
                                          {event.amount}
                                        </div>
                                      </Table.Cell>
                                      {/* <Table.Cell>
                            <div style={{
                              fontSize: "12px",
                              fontWeight: "normal",
                            }}>
                              {`$${event.usdValue}`}
                            </div>
                          </Table.Cell> */}
                                      <Table.Cell>
                                        <div style={{
                                          fontSize: "12px",
                                          fontWeight: "normal",
                                        }}>
                                          {event.date}
                                        </div>
                                      </Table.Cell>
                                    </Table.Row>
                                  ))}
                              </Table.Body>
                              {transferEventsWithDetails.length > 5 && <Table.Pagination
                                shadow
                                noMargin
                                align="center"
                                rowsPerPage={5}
                                onPageChange={(page) => console.log({ page })}
                              />}
                            </Table>
                          </div>

                        }



                      </div>

                    </Card>



                  </div>
                </TabPanel>
                <TabPanel header="Configure">
                  <RewardPacksSelector data={data} getData={getData} />
                </TabPanel>
                <TabPanel header="Add">
                  <AddReward getData={getData} />
                </TabPanel>
              </TabView>
            </div>
          </TabPanel>

          <TabPanel header="Smart Rewards (Archived)" >   {/* SMART REWARDS*/}
            <div
              style={{
                background: "linear-gradient(263deg, rgb(25 5 25 / 85%), rgb(15 5 25 / 85%)), linear-gradient(153deg,rgb(0, 0, 0), rgb(0, 0, 0) 19%, rgb(0, 0, 0))",
                boxShadow: 'none',
                padding: '10px',
                borderRadius: '20px',
                marginTop: '20px',
              }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>

                <div>
                  <div style={{ marginTop: "15px" }}>
                    Your Total Earned
                  </div>
                  <div style={infoStyle}>
                    {data?.contractData !== undefined
                      ? formatCurrencyExtended(
                        calculateTotalEarnedByVersion(data?.tokensListV3) +
                        (isV2Relevant() ? calculateTotalEarnedByVersion(data?.tokensListV2) : 0) +
                        (isV1Relevant() ? calculateTotalEarnedByVersion(data?.tokensListV1) : 0)
                      )
                      : LoadingContent()
                    }
                  </div>
                </div>

                <div>
                  <div style={{ marginTop: "15px" }}>
                    Total Distributed
                  </div>
                  <div style={infoStyle}>
                    {data?.contractData !== undefined
                      ? formatCurrencyExtended(
                        calculateTotalDistributedByVersion(data?.tokensListV3) +
                        calculateTotalDistributedByVersion(data?.tokensListV2) +
                        calculateTotalDistributedByVersion(data?.tokensListV1)
                      )
                      : LoadingContent()
                    }
                  </div>
                </div>


              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                <Card style={cardSmartRewardOutlineStyle}>
                  <div style={{ marginTop: "10px" }}>

                  </div>
                  <TabView>

                    <TabPanel header="Summary">
                      <div>

                        {/* <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>

                    <div>
                      <div style={{ marginTop: "15px" }}>
                        Total Earned
                      </div>
                      <div style={infoStyle}>
                        {data?.contractData !== undefined
                          ? formatCurrencyExtended(
                            calculateTotalEarnedByVersion(data?.tokensList) +
                            (isV2Relevant() ? calculateTotalEarnedByVersion(data?.tokensListV2) : 0) +
                            (isV1Relevant() ? calculateTotalEarnedByVersion(data?.tokensListV1) : 0)
                          )
                          : LoadingContent()
                        }
                      </div>
                    </div>

                    <div>
                      <div style={{ marginTop: "15px" }}>
                        Total Distributed
                      </div>
                      <div style={infoStyle}>
                        {data?.contractData !== undefined
                          ? formatCurrencyExtended(
                            calculateTotalDistributedByVersion(data?.tokensList) +
                            calculateTotalDistributedByVersion(data?.tokensListV2) +
                            calculateTotalDistributedByVersion(data?.tokensListV1)
                          )
                          : LoadingContent()
                        }
                      </div>
                    </div>
                  </div> */}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))', gap: '10px', marginTop: '10px' }}>
                          <div>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }} ref={refTotalEarnedV3}>
                              Total Earned by Version
                            </div>
                            <div style={{ ...miniInfoStyle, display: 'flex' }}>
                              v3: {inViewTotalEarnedV3 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensListV3)) : LoadingContent()}
                            </div>
                            {isV2Relevant() &&
                              <div style={{ ...miniInfoStyle, display: 'flex' }} ref={refTotalEarnedV2}>
                                v2: {inViewTotalEarnedV2 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensListV2)) : LoadingContent()}
                              </div>}
                            {isV1Relevant() &&
                              <div style={{ ...miniInfoStyle, display: 'flex' }} ref={refTotalEarnedV1}>
                                v1: {inViewTotalEarnedV1 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensListV1)) : LoadingContent()}
                              </div>}

                          </div>
                          <div>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }} ref={refTotalEarnedV3}>
                              Total Distributed by Version
                            </div>
                            <div style={{ ...miniInfoStyle, display: 'flex' }}>
                              v3: {inViewTotalEarnedV3 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalDistributedByVersion(data?.tokensListV3)) : LoadingContent()}
                            </div>
                            {//isV2Relevant() &&
                              <div style={{ ...miniInfoStyle, display: 'flex' }} ref={refTotalEarnedV2}>
                                v2: {inViewTotalEarnedV2 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalDistributedByVersion(data?.tokensListV2)) : LoadingContent()}
                              </div>}
                            {//isV1Relevant() &&
                              <div style={{ ...miniInfoStyle, display: 'flex' }} ref={refTotalEarnedV1}>
                                v1: {inViewTotalEarnedV1 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalDistributedByVersion(data?.tokensListV1)) : LoadingContent()}
                              </div>}
                          </div>
                        </div>

                        {
                          data?.contractData && consolidateTokens().filter(token => token.totalUSD > 0).length > 0 && (
                            <>
                              <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                                Total Rewards Summary
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '20px', marginTop: '10px', }}>
                                {
                                  consolidateTokens()
                                    .filter(token => token.totalUSD > 0)
                                    .sort((a, b) => b.totalUSD - a.totalUSD)
                                    .map((token, index) => (
                                      <div key={index} style={{
                                        ...miniInfoStyle, display: 'flex',
                                        flexDirection: 'column',
                                      }}>
                                        <div style={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                        }}>
                                          {formatTokenLabel(data.dbData, { value: token.name, label: token.label })}
                                          {formatCurrencyExtended(token.totalUSD)}

                                        </div>
                                        <div style={{
                                          fontSize: '12px',
                                          marginTop: '2px',
                                          color: 'grey',
                                          fontWeight: '200',
                                          //marginLeft: '5px',
                                          //increase letter spacing
                                          letterSpacing: '2px',
                                        }}>
                                          <strong>{token.symbol === "WBNB" ? "BNB" : token.symbol}</strong>
                                        </div>

                                        {/* <div style={{ marginTop: '4px', paddingRight: '15px' }} >
                              <Progress color="gradient" size="sm"
                                value={(token.totalUSD / totalEarned) * 100} />
                            </div> */}
                                      </div>
                                    ))
                                }
                              </div>
                            </>
                          )
                        }

                        {
                          data?.contractData && consolidateClaimableTokens().filter(token => token.totalUSD > 0).length > 0 && (
                            <>
                              <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                                All Claimable Rewards
                              </div>

                              <Grid.Container gap={2} justify="center">
                                {
                                  consolidateClaimableTokens()
                                    .filter(token => token.totalUSD > 0 && token.symbol !== "WBNB")
                                    .map((token, index) => (
                                      <Grid key={index}>
                                        <RevealButton
                                          color="gradient"
                                          size="lg"
                                          onClick={() => handleConsolidatedClaim(token)}

                                          disabled={claimingStates[token.name]} // Add a state variable to track if claiming is in progress
                                        >
                                          {claimingStates[token.name] ? (
                                            <Loading type="spinner" color="currentColor" size="sm" />
                                          ) : (
                                            <>
                                              <span>{formatTokenLabel(data.dbData, { value: token.name, label: token.label })}</span>
                                              <span>{`${formatDecimals(parseFloat(token.unclaimedAmount), token.decimals)} (${formatCurrencyExtended(token.totalUSD)})`}</span>
                                            </>
                                          )}
                                        </RevealButton>
                                        {/* <Button
                                    onClick={() => handleConsolidatedClaim(token)}
                                    rounded
                                    severity="warning"
                                    size="small"
                                    style={{ width: '150px', justifyContent: 'center', marginTop: '10px' }}
                                  >
                                    <span>{formatTokenLabel(data.dbData, { value: token.name, label: token.label })}</span>
                                    <span>{`${formatDecimals(parseFloat(token.unclaimedAmount), token.decimals)} (${formatCurrencyExtended(token.totalUSD)})`}</span>
                                  </Button> */}
                                      </Grid>

                                    ))
                                }

                              </Grid.Container>
                            </>
                          )
                        }




                      </div>
                    </TabPanel>

                    <TabPanel header="Current (V3)">
                      <div>

                        <div style={{ marginTop: "15px" }}>
                          Total Earned
                        </div>
                        <div style={infoStyle}>
                          {inViewTotalEarnedV3 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensList)) : LoadingContent()}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                          <div>
                            <div style={miniCardHeaderStyle}>
                              {data?.contractData !== undefined
                                ? 'Claimed'
                                : <LoadingTitle />
                              }
                            </div>
                            <div style={miniInfoStyle}>
                              {inViewTotalEarnedV3 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedClaimedByVersion(data?.tokensList)) : LoadingContent()}
                            </div>
                          </div>
                          <div>
                            <div style={miniCardHeaderStyle}>
                              {data?.contractData !== undefined
                                ? 'Unclaimed'
                                : <LoadingTitle />
                              }
                            </div>
                            <div style={miniInfoStyle}>
                              {inViewTotalEarnedV3 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedUnclaimedByVersion(data?.tokensList)) : LoadingContent()}
                            </div>
                          </div>
                        </div>
                      </div>
                      {calculateTotalEarnedClaimedByVersion(data?.tokensList) > 0 &&
                        (
                          <>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                              Reward Summary
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                              {data.tokensList &&
                                data.tokensList
                                  .map(token => ({
                                    name: token.value,
                                    claimedUSD: calculateClaimedValueUSD(token),
                                    symbol: token.label,
                                  }))
                                  .sort((a, b) => b.claimedUSD - a.claimedUSD)
                                  .filter((token) => token.claimedUSD > 0)
                                  .map((token, index) => (
                                    <div key={index} style={{
                                      ...miniInfoStyle, display: 'flex',
                                      flexDirection: 'column',
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                      }}>
                                        {formatTokenLabel(data.dbData, { value: token.name, label: token.label })}
                                        {formatCurrencyExtended(token.claimedUSD)}
                                      </div>

                                      <div style={{
                                        fontSize: '12px',
                                        marginTop: '2px',
                                        color: 'grey',
                                        fontWeight: '200',
                                        //marginLeft: '5px',
                                        //increase letter spacing
                                        letterSpacing: '2px',
                                      }}>
                                        <strong>{token.symbol === "WBNB" ? "BNB" : token.symbol}</strong>
                                      </div>


                                    </div>
                                  ))
                              }

                            </div>
                          </>
                        )}
                      {calculateTotalEarnedUnclaimedByVersion(data?.tokensList) > 0 &&
                        (
                          <>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                              Claimable Rewards
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginTop: '10px' }}>
                              {data.tokensList &&
                                data.tokensList
                                  .map(token => ({
                                    name: token.value,
                                    unclaimedUSD: calculateUnclaimedValueUSD(token),
                                    unclaimedAmount: token.unclaimedAmount,
                                    symbol: token.label,
                                    decimals: token.decimals,
                                  }))
                                  .sort((a, b) => b.unclaimedUSD - a.unclaimedUSD)
                                  .filter((token) => token.unclaimedUSD > 0 && token.symbol !== "WBNB")
                                  .map((token, index) => (
                                    <div key={index} style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>

                                      <Button
                                        onClick={() => handleClaim(contracts.smartRewards, token)}
                                        rounded
                                        severity="warning"
                                        size="small"
                                        style={{ width: '150px', justifyContent: 'center', marginTop: '10px' }}
                                      >
                                        <span>{formatTokenLabel(data.dbData, { value: token.name, label: token.label })}</span>
                                        <span>{`${formatDecimals(parseFloat(token.unclaimedAmount), token.decimals)} (${formatCurrencyExtended(token.unclaimedUSD)})`}</span>
                                      </Button>
                                    </div>
                                  ))
                              }
                            </div>
                          </>
                        )}

                    </TabPanel>

                    {isV2Relevant() && <TabPanel header="V2">
                      <div style={{ marginTop: "15px" }}>
                        Yourr Total Earned
                      </div>
                      <div style={infoStyle}>
                        {inViewTotalEarnedV2 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensListV2)) : LoadingContent()}
                      </div>

                      {
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                          <div>
                            <div style={miniCardHeaderStyle}>
                              {data?.contractData !== undefined
                                ? 'Claimed'
                                : <LoadingTitle />
                              }
                            </div>
                            <div style={miniInfoStyle}>
                              {inViewTotalEarnedV2 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedClaimedByVersion(data?.tokensListV2)) : LoadingContent()}
                            </div>
                          </div>
                          <div>
                            <div style={miniCardHeaderStyle}>
                              {data?.contractData !== undefined
                                ? 'Unclaimed'
                                : <LoadingTitle />
                              }
                            </div>
                            <div style={miniInfoStyle}>
                              {inViewTotalEarnedV2 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedUnclaimedByVersion(data?.tokensListV2)) : LoadingContent()}
                            </div>
                          </div>
                        </div>
                      }

                      {calculateTotalEarnedClaimedByVersion(data?.tokensListV2) > 0 &&
                        (
                          <>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                              Reward Summary
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                              {data.tokensListV2 &&
                                data.tokensListV2
                                  .map(token => ({
                                    name: token.value,
                                    claimedUSD: calculateClaimedValueUSD(token),
                                    symbol: token.label,
                                  }))
                                  .sort((a, b) => b.claimedUSD - a.claimedUSD)
                                  .filter((token) => token.claimedUSD > 0)
                                  .map((token, index) => (
                                    <div key={index} style={{
                                      ...miniInfoStyle, display: 'flex',
                                      flexDirection: 'column',
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                      }}>
                                        {formatTokenLabel(data.dbData, { value: token.name, label: token.label })}
                                        {formatCurrencyExtended(token.claimedUSD)}
                                      </div>

                                      <div style={{
                                        fontSize: '12px',
                                        marginTop: '2px',
                                        color: 'grey',
                                        fontWeight: '200',
                                        //marginLeft: '5px',
                                        //increase letter spacing
                                        letterSpacing: '2px',
                                      }}>
                                        <strong>{token.symbol === "WBNB" ? "BNB" : token.symbol}</strong>
                                      </div>


                                    </div>
                                  ))
                              }

                            </div>
                          </>
                        )}
                      {calculateTotalEarnedUnclaimedByVersion(data?.tokensListV2) > 0 &&
                        (
                          <>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                              Claimable Rewards
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginTop: '10px' }}>
                              {data.tokensListV2 &&
                                data.tokensListV2
                                  .map(token => ({
                                    name: token.value,
                                    unclaimedUSD: calculateUnclaimedValueUSD(token),
                                    unclaimedAmount: token.unclaimedAmount,
                                    symbol: token.label,
                                    decimals: token.decimals,
                                  }))
                                  .sort((a, b) => b.unclaimedUSD - a.unclaimedUSD)
                                  .filter((token) => token.unclaimedUSD > 0)
                                  .map((token, index) => (
                                    <div key={index} style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>
                                      <Button
                                        onClick={() => handleClaimLegacy(contracts.smartRewardsV2, token)}

                                        rounded
                                        severity="warning"
                                        size="small"
                                        style={{ width: '150px', justifyContent: 'center', marginTop: '10px' }}

                                      >
                                        <span>{formatTokenLabel(data.dbData, { value: token.name, label: token.label })}</span>
                                        <span>{`${formatDecimals(parseFloat(token.unclaimedAmount), token.decimals)} (${formatCurrencyExtended(token.unclaimedUSD)})`}</span>
                                      </Button>
                                    </div>
                                  ))
                              }
                            </div>
                          </>
                        )}
                    </TabPanel>}

                    {isV1Relevant() && <TabPanel header="V1">
                      <div style={{ marginTop: "15px" }}>
                        Your Total Earned
                      </div>
                      <div style={infoStyle}>
                        {inViewTotalEarnedV2 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedByVersion(data?.tokensListV1)) : LoadingContent()}
                      </div>

                      {
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                          <div>
                            <div style={miniCardHeaderStyle}>
                              {data?.contractData !== undefined
                                ? 'Claimed'
                                : <LoadingTitle />
                              }
                            </div>
                            <div style={miniInfoStyle}>
                              {inViewTotalEarnedV1 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedClaimedByVersion(data?.tokensListV1)) : LoadingContent()}
                            </div>
                          </div>
                          <div>
                            <div style={miniCardHeaderStyle}>
                              {data?.contractData !== undefined
                                ? 'Unclaimed'
                                : <LoadingTitle />
                              }
                            </div>
                            <div style={miniInfoStyle}>
                              {inViewTotalEarnedV1 && data?.contractData !== undefined ? formatCurrencyExtended(calculateTotalEarnedUnclaimedByVersion(data?.tokensListV1)) : LoadingContent()}
                            </div>
                          </div>
                        </div>
                      }

                      {calculateTotalEarnedClaimedByVersion(data?.tokensListV1) > 0 &&
                        (
                          <>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                              Reward Summary
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginTop: '10px' }}>
                              {data.tokensListV1 &&
                                data.tokensListV1
                                  .map(token => ({
                                    name: token.value,
                                    claimedUSD: calculateClaimedValueUSD(token),
                                    symbol: token.label,
                                  }))
                                  .sort((a, b) => b.claimedUSD - a.claimedUSD)
                                  .filter((token) => token.claimedUSD > 0)
                                  .map((token, index) => (
                                    <div key={index} style={{
                                      ...miniInfoStyle, display: 'flex',
                                      flexDirection: 'column',
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                      }}>
                                        {formatTokenLabel(data.dbData, { value: token.name, label: token.label })}
                                        {formatCurrencyExtended(token.claimedUSD)}
                                      </div>

                                      <div style={{
                                        fontSize: '12px',
                                        marginTop: '2px',
                                        color: 'grey',
                                        fontWeight: '200',
                                        //marginLeft: '5px',
                                        //increase letter spacing
                                        letterSpacing: '2px',
                                      }}>
                                        <strong>{token.symbol === "WBNB" ? "BNB" : token.symbol}</strong>
                                      </div>


                                    </div>
                                  ))
                              }

                            </div>
                          </>
                        )}
                      {calculateTotalEarnedUnclaimedByVersion(data?.tokensListV1) > 0 &&
                        (
                          <>
                            <div style={{ ...miniCardHeaderStyle, marginTop: "20px" }}>
                              Claimable Rewards
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginTop: '10px' }}>
                              {data.tokensListV1 &&
                                data.tokensListV1
                                  .map(token => ({
                                    name: token.value,
                                    unclaimedUSD: calculateUnclaimedValueUSD(token),
                                    unclaimedAmount: token.unclaimedAmount,
                                    symbol: token.label,
                                    decimals: token.decimals,
                                  }))
                                  .sort((a, b) => b.unclaimedUSD - a.unclaimedUSD)
                                  .filter((token) => token.unclaimedUSD > 0)
                                  .map((token, index) => (
                                    <div key={index} style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>
                                      <Button
                                        onClick={() => handleClaimLegacy(contracts.smartRewardsV1, token)}

                                        rounded
                                        severity="warning"
                                        size="small"
                                        style={{ width: '150px', justifyContent: 'center', marginTop: '10px' }}

                                      >
                                        <span>{formatTokenLabel(data.dbData, { value: token.name, label: token.label })}</span>
                                        <span>{`${formatDecimals(parseFloat(token.unclaimedAmount), token.decimals)} (${formatCurrencyExtended(token.unclaimedUSD)})`}</span>
                                      </Button>
                                    </div>
                                  ))
                              }
                            </div>
                          </>
                        )}
                    </TabPanel>}

                  </TabView>
                </Card>
                {/* <SmartRewardsSelector data={data} getData={getData} /> */}
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px', marginTop: '10px' }}>
        <div style={{ marginTop: '15px' }} >
          <h3>Top Selected Rewards</h3>
          <Table
            aria-label="Top Subscribed Tokens"
            css={{
              height: "auto",
              minWidth: "100%",
              backgroundColor: "white",
            }}
          >
            <Table.Header>
              <Table.Column>Rank</Table.Column>
              <Table.Column>Reward</Table.Column>
              <Table.Column>Address</Table.Column>
              {/* <Table.Column>Amount Pooled</Table.Column> */}
              <Table.Column>Percentage Pooled</Table.Column>
              <Table.Column>BNB Pending</Table.Column>
            </Table.Header>
            <Table.Body>
              {getTopSubscribedTokens().map((token, index) => (
                <Table.Row key={token.value}>
                  <Table.Cell>
                    <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {index + 1}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {formatTokenLabel(data?.dbData, { value: token.value, label: token.label })}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <AddressWithCopy address={token.address} />
                  </Table.Cell>
                  {/* <Table.Cell>
                        <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                          {formatBalanceWithIcon(
                            token.rocketFiSubscribed,
                            "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E",
                            data?.dbData,
                            0
                          )}
                        </div>
                      </Table.Cell> */}
                  <Table.Cell>
                    <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {formatPercentage(token?.rocketFiSubscribed / (data?.contractDataRp?.smartData?.smartRewardsTotalShares / 10 ** 9))}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    {token.bnbPending > 0 &&
                      <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                        <Button
                          size="small"
                          rounded
                          onClick={() => handleBuyReflectionsWithToken(token)}
                        >
                         {formatCurrencyExtended(token.bnbPending * getTokenPrice(data.tokensListRp, 'WBNB') / 10 ** 18)}
                        </Button>
                      </div>}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
            {<Table.Pagination
              shadow
              noMargin
              align="center"
              rowsPerPage={5}
              onPageChange={(page) => console.log({ page })}
            />}
          </Table>
        </div>
        <div style={{ marginTop: '15px' }} >
          <h3>Recently Added</h3>
          <Table
            aria-label="Top Subscribed Tokens"
            css={{
              height: "auto",
              minWidth: "100%",
              backgroundColor: "white",
            }}
          >
            <Table.Header>
              <Table.Column>Reward ID</Table.Column>
              <Table.Column>Reward</Table.Column>
              <Table.Column>Address</Table.Column>
              {/* <Table.Column>Amount Pooled</Table.Column> */}
              <Table.Column>Percentage Pooled</Table.Column>
            </Table.Header>
            <Table.Body>
              {getRecentlyAddedTokens().map((token, index) => (
                <Table.Row key={token.value}>
                  <Table.Cell>
                    <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {token.poolID}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {formatTokenLabel(data?.dbData, { value: token.value, label: token.label })}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <AddressWithCopy address={token.address} />
                  </Table.Cell>
                  {/* <Table.Cell>
                        <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                          {formatBalanceWithIcon(
                            token.rocketFiSubscribed,
                            "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E",
                            data?.dbData,
                            0
                          )}
                        </div>
                      </Table.Cell> */}
                  <Table.Cell>
                    <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                      {formatPercentage(token?.rocketFiSubscribed / (data?.contractDataRp?.smartData?.smartRewardsTotalShares / 10 ** 9))}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
            {<Table.Pagination
              shadow
              noMargin
              align="center"
              rowsPerPage={5}
              onPageChange={(page) => console.log({ page })}
            />}
          </Table>
        </div>
        <Toast ref={toast} />
      </div>

    </>
  );
}
export default RewardsModuleRewards;


const AddressWithCopy = ({ address }) => {
  const toast = useRef(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.current.show({ severity: 'success', summary: 'Copied', detail: 'Address copied to clipboard', life: 3000 });
    }, () => {
      toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to copy address', life: 3000 });
    });
  };

  return (
    <div style={{ display: 'flex-start', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
      {formatWalletAddress(address)}
      <Button icon="pi pi-copy" style={{
        border: 'none',
        backgroundColor: 'transparent',
        padding: '0px',
      }} onClick={() => copyToClipboard(address)} />
      <Toast ref={toast} />
    </div>
  );
};